/**
 * Alle offenen Deadlines, live und nach dem Bereichsfilter (F-09).
 *
 * Eine Abfrage fuer Kalender und Uebersicht - die eigentliche Logik steht
 * rein in `domain/deadlines.ts`. Widerspruechliche Fristen rechnen die Karten
 * selbst aus, sie brauchen nur ihre eigenen Eintraege.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';

import { db } from '@/db/schema';
import { collectDeadlines, type DeadlineItem } from '@/domain/deadlines';
import { useAreaFilter } from '@/store/areaFilter';
import { today } from '@/lib/datetime';
import type { ID } from '@/domain/types';

/** `nurBereich`: nur dieser Bereich, unabhaengig vom Filter (Bereichs-Board, F-01). */
export function useDeadlines(nurBereich?: ID): DeadlineItem[] {
  const { activeAreaIds } = useAreaFilter();

  const alle = useLiveQuery(
    async () => {
      const heute = today();
      const [tasks, projects, goals, exams] = await Promise.all([
        db.tasks.toArray(), db.projects.toArray(), db.goals.toArray(),
        // Klausuren (F-22): nur kommende - eine geschriebene ist nicht "ueberfaellig".
        db.events.filter((e) => !!e.klausur && String(e.start).slice(0, 10) >= heute).toArray(),
      ]);
      return collectDeadlines({ tasks, projects, goals, exams });
    },
    [],
    [] as DeadlineItem[],
  );

  return useMemo(() => {
    if (nurBereich) return alle.filter((d) => d.areaId === nurBereich);
    if (activeAreaIds.length === 0) return alle;
    return alle.filter((d) => d.areaId != null && activeAreaIds.includes(d.areaId));
  }, [alle, activeAreaIds, nurBereich]);
}
